/**
 * Health Check API Client
 * Provides system health monitoring and status information
 */

import { apiRequest } from './client';

export interface MoneyFlowHealthStats {
  totalAttempts: number;
  successful: number;
  failed: number;
  successRate: number;
  withData: number;
  withoutData: number;
  dataQualityScore: number;
  completeness: number;
  lastComputedAt: string;
  lastSuccessfulCompute: string | null;
  bySource: {
    cache: { attempts: number; success: number };
    api: { attempts: number; success: number };
    fallback: { attempts: number; success: number };
  };
  recentFailures: Array<{
    symbol: string;
    error?: string;
    timestamp: string;
    source: 'cache' | 'api' | 'fallback';
  }>;
  timestamp: string;
  // Upstream tracking (detects sentiment endpoint crashes)
  upstreamInvocations: number;
  upstreamReachRate: number;
}

export interface MoneyFlowHealth {
  status: 'healthy' | 'degraded' | 'critical';
  message: string;
  recommendations: string[];
  stats: MoneyFlowHealthStats;
  portfolioAvailability: {
    defaultSymbols: {
      available: number;
      total: number;
      percentage: number;
      missingSymbols: string[];
    };
    percentage: number;
    availableCount: number;
    totalCount: number;
    missingSymbols: string[];
  };
  thresholds: {
    minSuccessRate: number;
    minDataQualityScore: number;
    minAttemptsForStats: number;
    note: string;
  };
  lastChecked: string;
}

export interface SystemHealth {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  version: string;
  services: {
    yahooFinance: {
      status: 'healthy' | 'degraded' | 'unhealthy' | 'cooldown';
      circuitBreaker: 'CLOSED' | 'OPEN' | 'HALF_OPEN';
      rateLimitRemaining: number;
      cooldownEndsAt: string | null;
    };
    newsDataIO: {
      status: 'healthy' | 'degraded' | 'cooldown';
      dailyRequestsRemaining: number;
      cooldownEndsAt: string | null;
    };
    finnhub: {
      status: 'healthy';
      note: string;
    };
    cloudflareAI: {
      status: 'healthy';
      modelsAvailable: string[];
    };
    kvStorage: {
      status: 'healthy';
      namespaces: string[];
    };
    moneyFlowPool: {
      status: 'healthy' | 'degraded' | 'critical';
      completeness: number;
      fresh: number;
      stale: number;
      missing: number;
      totalSymbols: number;
      lastWarmAt: string | null;
      avgStalenessHours: number;
      issues: string[];
    };
  };
  configuration: {
    status: 'healthy' | 'degraded' | 'unhealthy';
    apiKeys: {
      NEWS_API_KEY: 'configured' | 'missing';
      FINNHUB_API_KEY: 'configured' | 'missing';
      FRED_API_KEY: 'configured' | 'missing';
      FMP_API_KEY: 'configured' | 'missing';
      X_API_KEY: 'configured' | 'missing';
    };
    issues: string[];
  };
  features: {
    portfolios: 'operational';
    sectorSentiment: 'operational' | 'limited' | 'unavailable';
    stockSentiment: 'operational' | 'limited' | 'unavailable';
    marketSentiment: 'operational' | 'limited' | 'unavailable';
  };
}

/**
 * Get overall system health status
 */
export async function getSystemHealth(): Promise<SystemHealth> {
  return await apiRequest<SystemHealth>('/health');
}

/**
 * Get money flow specific health status
 */
export async function getMoneyFlowHealth(): Promise<MoneyFlowHealth> {
  return await apiRequest<MoneyFlowHealth>('/health/money-flow');
}

/**
 * Get Yahoo Finance API health status
 */
export async function getYahooFinanceHealth() {
  return await apiRequest('/health/yahoo-finance');
}

/**
 * Quick check if system is healthy (boolean result)
 */
export async function isSystemHealthy(): Promise<boolean> {
  try {
    const health = await getSystemHealth();
    return health.status === 'healthy';
  } catch (error) {
    console.error('Failed to check system health:', error);
    return false;
  }
}

/**
 * Quick check if money flow system is healthy
 */
export async function isMoneyFlowHealthy(): Promise<boolean> {
  try {
    const health = await getMoneyFlowHealth();
    return health.status === 'healthy';
  } catch (error) {
    console.error('Failed to check money flow health:', error);
    return false;
  }
}

export interface CacheWarmRequest {
  portfolioId?: string;
  phases?: string; // e.g., 'market,portfolios'
}

export interface CacheWarmResponse {
  success: boolean;
  data: {
    status: 'completed' | 'partial' | 'failed';
    operations: {
      total: number;
      successful: number;
      failed: number;
    };
    parameters: {
      portfolioId?: string;
      phases: string;
    };
    errors?: string[];
    completedAt: string;
  };
  timestamp: string;
}

/**
 * Trigger manual cache warming (admin only)
 */
export async function warmCache(request: CacheWarmRequest): Promise<CacheWarmResponse> {
  return await apiRequest<CacheWarmResponse>('/admin/cache-warm', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Trigger cache warming for a specific portfolio
 */
export async function warmPortfolioCache(portfolioId: string): Promise<CacheWarmResponse> {
  return warmCache({
    portfolioId,
    phases: 'portfolios',
  });
}

/**
 * Trigger cache warming for market data
 */
export async function warmMarketCache(): Promise<CacheWarmResponse> {
  return warmCache({
    phases: 'market',
  });
}